/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import React, { ReactElement, cloneElement } from 'react';
import { Tooltip } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { EmployeePermissionsEnum } from '../../modules/administration/team/teamPermission/teamPermission.constant';
import { PERMISSION_MODE, PermissionCheckType, PermissionMode } from './permission.interface';
import { useUserPermission } from './useUserPermission';

interface PermissionWrapperProps {
  permissions: EmployeePermissionsEnum[];
  checkType?: PermissionCheckType;
  mode?: PermissionMode;
  noPermissionMessage?: string;
  children: ReactElement<any>; // Ensures it's a single React element
  enableTooltip?: boolean;
  disable?: boolean;
  disableDisplayType?: 'inline-block' | 'block';
}

const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  permissions,
  checkType = 'all',
  mode = PERMISSION_MODE.render,
  noPermissionMessage,
  children,
  enableTooltip = false,
  disable = false,
  disableDisplayType = 'inline-block',
}) => {
  const { checkPermission } = useUserPermission({ permissions, checkType });
  const hasPermission = checkPermission();
  const { t } = useTranslation();

  if (disable) {
    return children;
  }
  const permissionMessage = noPermissionMessage ?? t('common:noPermission');

  if (mode === PERMISSION_MODE.hide && !hasPermission) {
    return null;
  }

  if (mode === PERMISSION_MODE.message && !hasPermission) {
    return <div>{permissionMessage}</div>;
  }

  if (mode === PERMISSION_MODE.disable && !hasPermission) {
    const clonedElement = cloneElement(children, {
      ...children.props,
      onClick: undefined,
      onKeyDown: undefined,
      style: {
        ...children.props.style,
        opacity: 0.5,
        cursor: 'not-allowed',
        pointerEvents: 'none',
      },
      'aria-disabled': true,
    });

    if (enableTooltip) {
      return (
        <Tooltip label={permissionMessage} hasArrow fontSize="sm">
          <span style={{ display: disableDisplayType, cursor: 'not-allowed' }}>{clonedElement}</span>
        </Tooltip>
      );
    }
    return clonedElement;
  }

  return children;
};

export default PermissionWrapper;
